import { Route, Routes, Navigate } from 'react-router-dom';

import {
  MainLayout,
  DashboardLayout,
  GuestLayout,
  OnboardingLayout,
} from '@/layouts';
import {
  DashboardPage,
  ProfilePage,
  WhatsAppTemplatePage,
  EmailTemplatePage,
  ContactsPage,
  CampaignListPage,
  EmailCampaignListPage,
  EmailCampaignLogPage,
  CampaignLogPage,
  IntegrationsPage,
  IntegrationDetailPage,
  IntegrationHistoryPage,
  InstagramBusinessDetailPage,
  GoogleWorkspaceDetailPage,
  OtherEmailDetailPage,
  GoogleWorkspaceCallbackPage,
  WindexConnectPage,
  EmployeesPage,
  SettingsPage,
} from '@/modules/dashboard';
import {
  CRMContactsPage,
  CRMContactOverviewPage,
  CRMSegmentsPage,
  CRMSettingsPage,
  CRMPipelineSettingsPage,
  CRMDashboardPage,
  CRMTasksPage,
  CRMFollowUpsPage,
  FeedbackPage,
  WhatsAppChatPage,
  EmailInboxPage,
  WhatsAppFlowsPage,
  AppointmentsPage,
  BookingAvailabilityPage,
  RemindersPage,
  ReminderImportPage,
  ReminderImportJobPage,
  QuotationsPage,
  QuotationDetailPage,
  QuotationEditPage,
  QuotationProductCreatePage,
  QuotationProductEditPage,
  CustomerQuotationDetailPage,
} from '@/modules/dashboard';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  VerifyOtpPage,
  ResetPasswordPage,
  ChooseIntegrationPage,
  WhatsAppSetupPage,
  WhatsAppActivationPage,
  WhatsAppActivatedPage,
  PaymentSuccessPage,
  CompanyRegistrationPage,
} from '@/modules/Authentication';
import { PublicBusinessCardPage } from '@/modules/public';

import {
  PermissionGuard,
  AccessDenied,
  NotFound,
  ServerError,
} from '@/common/components';
import { PERMISSIONS } from '@/common/config/permissions';

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route element={<MainLayout />}>
        <Route index element={<Navigate to="/login" replace />} />

        {/* Guest-only Auth Routes */}
        <Route element={<GuestLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="verify-otp" element={<VerifyOtpPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Onboarding routes — step order + resume enforced by OnboardingLayout */}
        <Route element={<OnboardingLayout />}>
          <Route
            path="company-registration"
            element={<CompanyRegistrationPage />}
          />
          <Route
            path="choose-integration"
            element={<ChooseIntegrationPage />}
          />
          <Route path="whatsapp-setup" element={<WhatsAppSetupPage />} />
          <Route
            path="whatsapp-activation"
            element={<WhatsAppActivationPage />}
          />
          <Route
            path="whatsapp-activated"
            element={<WhatsAppActivatedPage />}
          />
          <Route path="payment-success" element={<PaymentSuccessPage />} />
        </Route>
      </Route>

      {/* Authenticated Dashboard Panel Layout */}
      <Route element={<DashboardLayout />}>
        <Route
          path="dashboard"
          element={
            <PermissionGuard
              permission={PERMISSIONS.DASHBOARD.VIEW}
              fallback={<AccessDenied />}
            >
              <DashboardPage />
            </PermissionGuard>
          }
        />

        {/* Integrations */}
        <Route
          path="integrations"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <IntegrationsPage />
            </PermissionGuard>
          }
        />
        <Route
          path="integrations/instagram-business"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <InstagramBusinessDetailPage />
            </PermissionGuard>
          }
        />
        <Route
          path="integrations/google-workspace"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <GoogleWorkspaceDetailPage />
            </PermissionGuard>
          }
        />
        <Route
          path="integrations/windex"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <WindexConnectPage />
            </PermissionGuard>
          }
        />
        <Route
          path="integrations/other-email"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <OtherEmailDetailPage />
            </PermissionGuard>
          }
        />
        <Route
          path="integrations/other_email"
          element={<Navigate to="/integrations/other-email" replace />}
        />
        <Route
          path="integrations/:appId"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <IntegrationDetailPage />
            </PermissionGuard>
          }
        />
        <Route
          path="integrations/:appId/history"
          element={
            <PermissionGuard
              permission={PERMISSIONS?.INTEGRATION?.VIEW || 'integration.view'}
              fallback={<AccessDenied />}
            >
              <IntegrationHistoryPage />
            </PermissionGuard>
          }
        />

        <Route
          path="ecommerce"
          element={
            <div className="p-6 text-center text-text-muted font-sf-pro-display bg-layer-5-card border border-border-default rounded-2xl">
              E-commerce View Placeholder
            </div>
          }
        />
        <Route
          path="calendar"
          element={
            <PermissionGuard
              permission={PERMISSIONS.APPOINTMENTS.VIEW}
              fallback={<AccessDenied />}
            >
              <AppointmentsPage />
            </PermissionGuard>
          }
        />
        <Route
          path="calendar/availability"
          element={
            <PermissionGuard
              permission={PERMISSIONS.APPOINTMENTS.VIEW}
              requiredRole="owner"
              fallback={<AccessDenied />}
            >
              <BookingAvailabilityPage />
            </PermissionGuard>
          }
        />
        <Route path="task" element={<Navigate to="/reminders" replace />} />
        <Route
          path="reminders"
          element={
            <PermissionGuard
              permission={PERMISSIONS.REMINDERS.VIEW}
              fallback={<AccessDenied />}
            >
              <RemindersPage />
            </PermissionGuard>
          }
        />
        <Route
          path="reminders/import"
          element={
            <PermissionGuard
              permission={PERMISSIONS.REMINDERS.CREATE}
              fallback={<AccessDenied />}
            >
              <ReminderImportPage />
            </PermissionGuard>
          }
        />
        <Route
          path="reminders/import/:jobId"
          element={
            <PermissionGuard
              permission={PERMISSIONS.REMINDERS.CREATE}
              fallback={<AccessDenied />}
            >
              <ReminderImportJobPage />
            </PermissionGuard>
          }
        />

        {/* CRM / Leads */}
        <Route
          path="contacts"
          element={
            <PermissionGuard
              permission={PERMISSIONS.CONTACTS.VIEW}
              fallback={<AccessDenied />}
            >
              <ContactsPage />
            </PermissionGuard>
          }
        />

        {/* Employees / Members */}
        <Route
          path="employees"
          element={
            <PermissionGuard
              permission={PERMISSIONS.MEMBERS.VIEW}
              fallback={<AccessDenied />}
            >
              <EmployeesPage />
            </PermissionGuard>
          }
        />

        {/* WA Templates */}
        <Route
          path="templates"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_TEMPLATES.VIEW}
              fallback={<AccessDenied />}
            >
              <WhatsAppTemplatePage />
            </PermissionGuard>
          }
        />

        {/* Email Templates */}
        <Route
          path="email-templates"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_TEMPLATES.VIEW}
              fallback={<AccessDenied />}
            >
              <EmailTemplatePage />
            </PermissionGuard>
          }
        />

        {/* WA Campaigns */}
        <Route
          path="campaigns"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_CAMPAIGNS.VIEW}
              fallback={<AccessDenied />}
            >
              <CampaignListPage />
            </PermissionGuard>
          }
        />
        <Route
          path="email-campaigns"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_CAMPAIGNS.VIEW}
              fallback={<AccessDenied />}
            >
              <EmailCampaignListPage />
            </PermissionGuard>
          }
        />
        <Route
          path="email-campaigns/:campaignId"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_CAMPAIGNS.VIEW}
              fallback={<AccessDenied />}
            >
              <EmailCampaignLogPage />
            </PermissionGuard>
          }
        />
        <Route
          path="campaigns/:campaignId"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_CAMPAIGNS.VIEW}
              fallback={<AccessDenied />}
            >
              <CampaignLogPage />
            </PermissionGuard>
          }
        />

        {/* CRM Sub-pages (assuming CRM Contacts is LEAD_DETAILS or LEADS) */}
        <Route
          path="crm-dashboard"
          element={
            <PermissionGuard
              permission={PERMISSIONS.DASHBOARD.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMDashboardPage />
            </PermissionGuard>
          }
        />
        <Route
          path="crm-dashboard/task"
          element={
            <PermissionGuard
              permission={PERMISSIONS.DASHBOARD.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMTasksPage />
            </PermissionGuard>
          }
        />
        <Route
          path="crm-dashboard/tasks"
          element={<Navigate to="/crm-dashboard/task" replace />}
        />
        <Route
          path="crm-dashboard/followup"
          element={
            <PermissionGuard
              permission={PERMISSIONS.DASHBOARD.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMFollowUpsPage />
            </PermissionGuard>
          }
        />
        <Route
          path="crm-dashboard/follow-up"
          element={<Navigate to="/crm-dashboard/followup" replace />}
        />
        <Route
          path="crm-dashboard/followups"
          element={<Navigate to="/crm-dashboard/followup" replace />}
        />

        <Route
          path="crm-contacts"
          element={
            <PermissionGuard
              permission={PERMISSIONS.LEADS.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMContactsPage />
            </PermissionGuard>
          }
        />
        <Route
          path="crm/contacts"
          element={<Navigate to="/crm-contacts" replace />}
        />
        <Route
          path="crm/leads"
          element={<Navigate to="/crm-contacts" replace />}
        />
        <Route
          path="crm-contacts/overview"
          element={
            <PermissionGuard
              permission={PERMISSIONS.LEADS.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMContactOverviewPage />
            </PermissionGuard>
          }
        />

        <Route path="crm-pipeline" element={null} />
        <Route
          path="crm-pipeline/settings"
          element={
            <PermissionGuard
              permission={PERMISSIONS.CRM_CONFIG.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMPipelineSettingsPage />
            </PermissionGuard>
          }
        />

        <Route
          path="crm-segments"
          element={
            <PermissionGuard
              permission={PERMISSIONS.SEGMENTS.VIEW}
              fallback={<AccessDenied />}
            >
              <CRMSegmentsPage />
            </PermissionGuard>
          }
        />

        <Route
          path="crm-settings"
          element={
            <PermissionGuard
              permission={[
                PERMISSIONS.TAGS.VIEW,
                PERMISSIONS.CUSTOM_FIELDS.VIEW,
                PERMISSIONS.AUTO_ASSIGNMENT.VIEW,
              ]}
              fallback={<AccessDenied />}
            >
              <CRMSettingsPage />
            </PermissionGuard>
          }
        />

        {/* Settings */}
        <Route
          path="settings"
          element={
            <PermissionGuard
              permission={PERMISSIONS.PROFILE.VIEW}
              fallback={<AccessDenied />}
            >
              <SettingsPage />
            </PermissionGuard>
          }
        />
        <Route
          path="profile"
          element={
            <PermissionGuard
              permission={PERMISSIONS.PROFILE.VIEW}
              fallback={<AccessDenied />}
            >
              <ProfilePage />
            </PermissionGuard>
          }
        />

        <Route path="settings/feedback" element={<FeedbackPage />} />

        {/* WA Chats & Flows */}
        <Route
          path="whatsapp-chat"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_CHATS.VIEW}
              fallback={<AccessDenied />}
            >
              <WhatsAppChatPage />
            </PermissionGuard>
          }
        />

        <Route
          path="email-inbox"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_CHATS.VIEW}
              fallback={<AccessDenied />}
            >
              <EmailInboxPage />
            </PermissionGuard>
          }
        />

        <Route
          path="whatsapp-flows"
          element={
            <PermissionGuard
              permission={PERMISSIONS.WA_FLOWS.VIEW}
              fallback={<AccessDenied />}
            >
              <WhatsAppFlowsPage />
            </PermissionGuard>
          }
        />

        {/* Quotations */}
        <Route
          path="quotations"
          element={
            <PermissionGuard
              permission={PERMISSIONS.QUOTATIONS.VIEW}
              fallback={<AccessDenied />}
            >
              <QuotationsPage />
            </PermissionGuard>
          }
        />

        <Route
          path="quotations/:quotationId/edit"
          element={
            <PermissionGuard
              permission={PERMISSIONS.QUOTATIONS.UPDATE}
              fallback={<AccessDenied />}
            >
              <QuotationEditPage />
            </PermissionGuard>
          }
        />
        <Route
          path="quotations/:quotationId"
          element={
            <PermissionGuard
              permission={PERMISSIONS.QUOTATIONS.VIEW}
              fallback={<AccessDenied />}
            >
              <QuotationDetailPage />
            </PermissionGuard>
          }
        />
        <Route
          path="quotations/customer/:customerQuotationId"
          element={
            <PermissionGuard
              permission={PERMISSIONS.QUOTATIONS.VIEW}
              fallback={<AccessDenied />}
            >
              <CustomerQuotationDetailPage />
            </PermissionGuard>
          }
        />
        <Route
          path="quotations/:quotationId/products/create"
          element={
            <PermissionGuard
              permission={PERMISSIONS.QUOTATIONS.CREATE}
              fallback={<AccessDenied />}
            >
              <QuotationProductCreatePage />
            </PermissionGuard>
          }
        />
        <Route
          path="quotations/:quotationId/products/:productId/edit"
          element={
            <PermissionGuard
              permission={PERMISSIONS.QUOTATIONS.UPDATE}
              fallback={<AccessDenied />}
            >
              <QuotationProductEditPage />
            </PermissionGuard>
          }
        />

        {/* Legacy redirects */}
        <Route
          path="crm-tags"
          element={<Navigate to="/crm-settings" replace />}
        />
        <Route
          path="crm-custom-fields"
          element={<Navigate to="/crm-settings" replace />}
        />

        {/* Error pages inside dashboard layout */}
        <Route path="not-found" element={<NotFound />} />
        <Route
          path="server-error"
          element={<ServerError onRetry={() => window.location.reload()} />}
        />
      </Route>

      {/* Public Business Card Route */}
      <Route path="/:shortId" element={<PublicBusinessCardPage />} />

      {/* Google Workspace OAuth Callback — outside DashboardLayout (no sidebar during OAuth return) */}
      <Route
        path="/integrations/google-workspace/callback"
        element={<GoogleWorkspaceCallbackPage />}
      />

      {/* Public Quotation Route */}
      <Route
        path="/quotations/share/:shortId"
        element={<CustomerQuotationDetailPage />}
      />

      {/* Catch-all route for undefined paths */}
      <Route
        path="*"
        element={
          localStorage.getItem('access_token') ? (
            <Navigate to="/not-found" replace />
          ) : (
            <NotFound />
          )
        }
      />
    </Routes>
  );
}
